/**
 * HerBellaVita — Cart upsells controller.
 * Sin formularios: el click se delega a nivel del componente.
 */

import { CartAddEvent, CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';

class HBVCartUpsells extends HTMLElement {
  constructor() {
    super();
    this._refs = {};
    this._cache = new Map();
  }

  connectedCallback() {
    this._captureRefs();

    this._locale = this.dataset.locale || (document.documentElement.lang || 'es');
    this._limit = parseInt(this.dataset.limit || '3', 10);
    this._currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    this._mode = this.dataset.mode || 'manual';

    // Delegación de click: capturamos clicks en cualquier botón [data-upsell-add]
    // dentro del componente. Sobrevive a re-renders sin tener que re-bindear.
    if (!this._clickHandler) {
      this._clickHandler = (e) => {
        const btn = e.target.closest('[data-upsell-add]');
        if (btn && this.contains(btn)) {
          e.preventDefault();
          e.stopPropagation();
          this._handleAdd(btn);
        }
      };
      this.addEventListener('click', this._clickHandler);
    }

    if (this._mode === 'auto') {
      this._renderAuto();
    }

    this._onCartChange = (e) => {
      if (e.type === DiscountUpdateEvent.eventName && this._mode === 'auto') {
        this._renderAuto();
      }
    };
    document.addEventListener(CartAddEvent.eventName, this._onCartChange);
    document.addEventListener(CartUpdateEvent.eventName, this._onCartChange);
    document.addEventListener(DiscountUpdateEvent.eventName, this._onCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this._onCartChange);
    document.removeEventListener(CartUpdateEvent.eventName, this._onCartChange);
    document.removeEventListener(DiscountUpdateEvent.eventName, this._onCartChange);
  }

  _captureRefs() {
    this.querySelectorAll('[ref]').forEach((el) => {
      this._refs[el.getAttribute('ref')] = el;
    });
  }

  async _handleAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const card = btn.closest('.hbv-upsell');
    const labelEl = btn.querySelector('[data-label]');

    btn.dataset.loading = 'true';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this._sectionsToRefresh(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Error al añadir');
      }

      const data = await res.json();

      delete btn.dataset.loading;
      btn.dataset.added = 'true';
      if (labelEl) labelEl.textContent = 'Añadido';

      // Animar salida de la card mientras llega el morph
      if (card) {
        card.dataset.removing = 'true';
      }

      document.dispatchEvent(
        new CartAddEvent(data, btn, {
          source: 'hbv-cart-upsells',
          didError: false,
        })
      );
    } catch (err) {
      delete btn.dataset.loading;
      if (labelEl) labelEl.textContent = 'Error';
      setTimeout(() => { if (labelEl) labelEl.textContent = 'Agregar'; }, 2000);
      console.error('[hbv-cart-upsells]', err);
    }
  }

  /* ---------- Modo AUTO (fallback algorítmico) ---------- */

  async _renderAuto() {
    const list = this._refs.hbvUpsellList;
    if (!list || this._mode !== 'auto') return;

    const idsStr = this.dataset.productIds || '';
    const ids = idsStr.split(',').map((s) => s.trim()).filter(Boolean);

    if (ids.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }
    this.removeAttribute('hidden');

    const allRecs = (
      await Promise.all(ids.map((id) => this._fetchRecommendations(id)))
    ).flat();

    const seen = new Set(ids);
    const products = [];
    for (const p of allRecs) {
      const pid = String(p.id);
      if (seen.has(pid)) continue;
      seen.add(pid);
      products.push(p);
      if (products.length >= this._limit) break;
    }

    if (products.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    const frag = document.createDocumentFragment();
    for (const product of products) {
      const card = this._buildAutoCard(product);
      if (card) frag.appendChild(card);
    }
    list.replaceChildren(frag);
  }

  async _fetchRecommendations(productId) {
    if (this._cache.has(productId)) return this._cache.get(productId);

    const tryIntent = async (intent) => {
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(
        productId
      )}&intent=${intent}&limit=6`;
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data.products) ? data.products : [];
      } catch (e) {
        return [];
      }
    };

    let products = await tryIntent('complementary');
    if (products.length === 0) products = await tryIntent('related');

    this._cache.set(productId, products);
    return products;
  }

  _buildAutoCard(product) {
    const tpl = this._refs.hbvUpsellTemplate;
    if (!tpl) return null;
    const node = tpl.content.firstElementChild.cloneNode(true);

    const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
    if (!variant) return null;

    const img = product.featured_image || (product.images && product.images[0]) || null;
    const price = variant.price;
    const compare = variant.compare_at_price && Number(variant.compare_at_price) > Number(price)
      ? variant.compare_at_price
      : null;

    node.setAttribute('data-upsell-product-id', String(product.id));
    const titleEl = node.querySelector('[data-title]');
    if (titleEl) titleEl.textContent = product.title;

    const imgEl = node.querySelector('[data-src]');
    if (imgEl) {
      if (img) { imgEl.src = img; imgEl.alt = product.title; }
      else { imgEl.remove(); }
    }

    node.querySelector('[data-price]').textContent = this._formatMoney(price);
    const compareEl = node.querySelector('[data-compare]');
    const pctEl = node.querySelector('[data-pct]');
    if (compare && compareEl && pctEl) {
      compareEl.textContent = this._formatMoney(compare);
      compareEl.hidden = false;
      const pct = Math.round((1 - Number(price) / Number(compare)) * 100);
      pctEl.textContent = `-${pct}%`;
      pctEl.hidden = false;
    }

    const addBtn = node.querySelector('[data-upsell-add]');
    if (addBtn) addBtn.dataset.variantId = variant.id;

    return node;
  }

  _sectionsToRefresh() {
    const ids = new Set();
    document
      .querySelectorAll('cart-items-component[data-section-id], cart-icon-component[data-section-id]')
      .forEach((el) => {
        const id = el.dataset.sectionId;
        if (id) ids.add(id);
      });
    return Array.from(ids);
  }

  _formatMoney(cents) {
    const amount = Number(cents) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    try {
      return new Intl.NumberFormat(this._locale || 'es-ES', {
        style: 'currency', currency: this._currency,
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2).replace('.', ',')} €`;
    }
  }
}

if (!customElements.get('hbv-cart-upsells')) {
  customElements.define('hbv-cart-upsells', HBVCartUpsells);
}
